import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Divider,
  useTheme
} from '@mui/material';
import {
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';

const TwoFactorInput = ({ 
  onSubmit, 
  loading = false, 
  error = '', 
  onResend = null,
  autoFocus = true,
  showTrustDevice = true
}) => {
  const [code, setCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(30); // 30 second countdown for next code
  const [trustDevice, setTrustDevice] = useState(false);
  const inputRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    // Start countdown timer
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    if (value.length <= 6) {
      setCode(value);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.length === 6 && onSubmit) {
      onSubmit(code, trustDevice);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && code.length === 6) {
      handleSubmit(e);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 400, mx: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          Two-Factor Authentication
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Enter the 6-digit code from your authenticator app
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          ref={inputRef}
          fullWidth
          value={code}
          onChange={handleCodeChange}
          onKeyPress={handleKeyPress}
          placeholder="000000"
          disabled={loading}
          inputProps={{
            maxLength: 6,
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              letterSpacing: '0.8rem',
              fontFamily: 'monospace'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SecurityIcon color="action" />
              </InputAdornment>
            )
          }}
          sx={{ mb: 2 }}
          helperText={`Enter all 6 digits (${code.length}/6)`}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading || code.length !== 6}
          sx={{ mb: 2 }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Verifying...
            </>
          ) : (
            'Verify Code'
          )}
        </Button>

        {showTrustDevice && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Device Options
              </Typography>
            </Divider>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  disabled={loading}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ComputerIcon fontSize="small" />
                  <Typography variant="body2">
                    Don't challenge me for 30 days on this computer
                  </Typography>
                </Box>
              }
              sx={{ mb: 2 }}
            />
          </>
        )}

        {onResend && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Code expires in {formatTime(timeLeft)}
            </Typography>
            
            {timeLeft === 0 ? (
              <Button
                size="small"
                onClick={onResend}
                disabled={loading}
                startIcon={<RefreshIcon />}
              >
                Get New Code
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Wait {formatTime(timeLeft)} for a new code
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 3, p: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" sx={{ 
          color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'text.secondary'
        }}>
          <strong>Tip:</strong> Open your authenticator app and look for the 6-digit code 
          for "Expense Tracker". The code refreshes every 30 seconds.
        </Typography>
      </Box>
    </Paper>
  );
};

export default TwoFactorInput; 